/**
 * LevelFormPage - Página de criação/edição de nível de acesso
 *
 * Seções:
 * 1. Informações Básicas (nome, descrição)
 * 2. Permissões de Módulos
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLevel, useCreateLevel, useUpdateLevel } from '../hooks/useLevels';
import { useModules } from '../hooks/useModules';
import { Button } from '@/shared/components/ui/Button';
import { Select } from '@/shared/components/ui/Select';
import { Breadcrumb, Input, Textarea, type SelectOptionGroup } from '@/shared/components/ui';
import { PermissionSelector } from '../components/PermissionSelector';
import type { CreateLevelRequest, UpdateLevelRequest, ModulePermission, LandingPage } from '@troia-v3/types';
import { resolveLandingPageModule } from '@troia-v3/types';
import { Icon } from '@/shared/icons';

type LandingPageGroup = 'Dashboards' | 'Módulos' | 'Rotas livres';

interface LandingPageOption {
  value: LandingPage;
  label: string;
  group: LandingPageGroup;
}

/** Ordem e rótulos do mockup 14 (lote-b/14-nivel-tela-inicial.html). */
const LANDING_PAGE_OPTIONS: LandingPageOption[] = [
  { value: 'dashboards-support', label: 'Dashboard de atendimento', group: 'Dashboards' },
  { value: 'dashboards-commercial', label: 'Dashboard comercial', group: 'Dashboards' },
  { value: 'dashboards-marketing', label: 'Dashboard de marketing', group: 'Dashboards' },
  { value: 'dashboards-analytics', label: 'Analytics de visitas', group: 'Dashboards' },
  { value: 'crm', label: 'CRM (kanban)', group: 'Módulos' },
  { value: 'crm-seller', label: 'Dashboards — Minhas Vendas', group: 'Módulos' },
  { value: 'chat', label: 'Atendimento (chat)', group: 'Módulos' },
  { value: 'tickets', label: 'Tickets', group: 'Módulos' },
  { value: 'calendar', label: 'Agenda', group: 'Módulos' },
  { value: 'contacts', label: 'Contatos', group: 'Módulos' },
  { value: 'checklists-my', label: 'Meus checklists', group: 'Rotas livres' },
];

const LANDING_PAGE_GROUP_ORDER: LandingPageGroup[] = ['Dashboards', 'Módulos', 'Rotas livres'];

export const LevelFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  // Fetch level data (only in edit mode)
  const { data: level, isLoading, isError } = useLevel(id!);
  const createMutation = useCreateLevel();
  const updateMutation = useUpdateLevel();

  // Fetch modules to validate permissions
  const { data: modulesData } = useModules();

  // Form state
  const [formData, setFormData] = useState<Partial<CreateLevelRequest & UpdateLevelRequest>>({
    name: '',
    description: '',
    permissions: {},
    landingPage: undefined,
  });
  const [hasChanges, setHasChanges] = useState(!isEditMode); // New levels always have changes

  // Update form data when level loads (edit mode)
  React.useEffect(() => {
    if (level && modulesData) {
      // Get valid module names
      const validModuleNames = (modulesData.all || []).map((m: any) => m.name);

      // Filter out invalid module permissions
      const validPermissions: Record<string, ModulePermission> = {};
      Object.entries(level.permissions).forEach(([moduleId, permission]) => {
        if (validModuleNames.includes(moduleId)) {
          validPermissions[moduleId] = permission;
        }
      });

      setFormData({
        name: level.name,
        description: level.description,
        permissions: validPermissions, // ← Use filtered permissions
        landingPage: level.landingPage,
      });
      setHasChanges(false);
    }
  }, [level, modulesData]);

  // Handle form field changes
  const handleFieldChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  // Validation
  const validateForm = (): string | null => {
    if (!formData.name?.trim()) return 'Nome é obrigatório';
    if (formData.name.trim().length < 3) return 'Nome deve ter pelo menos 3 caracteres';

    return null;
  };

  // FiSave changes
  const handleSave = async () => {
    if (!hasChanges) return;

    const validationError = validateForm();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      if (isEditMode) {
        // Update existing level
        await updateMutation.mutateAsync({
          id: id!,
          data: formData as UpdateLevelRequest
        });
        setHasChanges(false);
        navigate('/settings/levels');
      } else {
        // Create new level
        await createMutation.mutateAsync(formData as CreateLevelRequest);
        navigate('/settings/levels');
      }
    } catch (error: any) {
      console.error('❌ Erro ao salvar nível:', error);
      alert('Erro ao salvar nível. Tente novamente.');
    }
  };

  // Loading state (only for edit mode)
  if (isEditMode && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Icon name="loader" className="w-8 h-8 animate-spin" style={{ color: 'var(--theme-button-primary)' }} />
      </div>
    );
  }

  // Error state (only for edit mode)
  if (isEditMode && (isError || !level)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4">
        <p className="text-lg" style={{ color: 'var(--theme-error-color)' }}>
          Erro ao carregar nível
        </p>
        <Button onClick={() => window.location.reload()} variant="secondary">
          Tentar Novamente
        </Button>
      </div>
    );
  }

  /**
   * Tela de módulo/alias só é selecionável com `read` no módulo DONO
   * (`resolveLandingPageModule`: `crm-seller` → `crm`); rota livre sempre.
   * Mockup 14: a opção fica DESABILITADA (não escondida) — o admin vê que ela
   * existe e o texto abaixo diz o que liberar.
   */
  const canReadLandingPage = (value: LandingPage): boolean => {
    const moduleId = resolveLandingPageModule(value);
    if (moduleId === null) return true;
    const permissions = formData.permissions as Record<string, ModulePermission> | undefined;
    return permissions?.[moduleId]?.actions?.includes('read') ?? false;
  };

  const landingPageGroups: SelectOptionGroup[] = LANDING_PAGE_GROUP_ORDER.map((group) => ({
    label: group,
    options: LANDING_PAGE_OPTIONS.filter((opt) => opt.group === group).map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: !canReadLandingPage(opt.value),
    })),
  }));

  const sellerSelected = formData.landingPage === 'crm-seller';
  const sellerAllowed = canReadLandingPage('crm-seller');

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-10"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderBottom: '1px solid var(--theme-card-border)',
        }}
      >
        <div className="px-4 h-14 flex flex-col justify-center">
          <div className="flex items-center justify-between">
            {/* Breadcrumb (F25-31: shared — mobile = legenda condensada sob o título, NUNCA seta voltar) */}
            <Breadcrumb
              items={[
                { label: 'Configurações', onNavigate: () => navigate('/settings'), testid: 'levels-form-breadcrumb-settings' },
                { label: 'Níveis de Acesso', onNavigate: () => navigate('/settings/levels'), testid: 'levels-form-breadcrumb' },
                { label: isEditMode ? (level ? level.name : 'Editar nível') : 'Novo nível', testid: 'levels-form-breadcrumb-current' },
              ]}
              mobileLegend="Configurações · Nível de acesso"
              data-testid="levels-form-breadcrumb-nav"
            />

            <div className="flex items-center gap-3">
              {hasChanges && (
                <span
                  className="text-sm hidden sm:inline"
                  style={{ color: 'var(--theme-warning-color)' }}
                >
                  {isEditMode ? 'Alterações não salvas' : 'Preencha os campos obrigatórios'}
                </span>
              )}
              <Button
                variant="secondary"
                className="hidden sm:inline-flex"
                onClick={() => navigate('/settings/levels')}
                data-testid="levels-form-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                disabled={!hasChanges || createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending || updateMutation.isPending ? (
                  <>
                    <Icon name="loader" className="w-4 h-4 mr-2 animate-spin" />
                    {isEditMode ? 'Salvando...' : 'Criando...'}
                  </>
                ) : (
                  <>
                    <Icon name="save" className="w-4 h-4 mr-2" />
                    {isEditMode ? 'Salvar Alterações' : 'Criar Nível'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        <div className="space-y-6">
          {/* 1. Basic Info Section */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div
              className="flex items-start gap-3 px-[18px] py-[15px] border-b"
              style={{ borderColor: 'var(--theme-card-border)' }}
            >
              <div
                className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: 'var(--theme-primary-background)',
                  color: 'var(--theme-primary)',
                }}
              >
                <Icon name="shield" className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold" style={{ color: 'var(--theme-card-text)' }}>
                  Informações Básicas
                </h2>
                <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                  Nome e descrição do nível de acesso
                </p>
              </div>
            </div>

            <div className="space-y-4 p-[18px]">
              {/* Nome */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Nome do Nível *
                </label>
                <Input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="Ex: Administrador, Supervisor, Atendente"
                  data-testid="level-form-name"
                />
              </div>

              {/* Descrição */}
              <div>
                <label
                  className="block text-sm font-medium mb-2"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  Descrição
                </label>
                <Textarea
                  value={formData.description || ''}
                  onChange={(e) => handleFieldChange('description', e.target.value)}
                  placeholder="Descreva as responsabilidades deste nível"
                  rows={3}
                  resize="none"
                  data-testid="level-form-description"
                />
              </div>

              {/* Tela Inicial — mockup 14 (lote-b/14-nivel-tela-inicial.html) */}
              <div>
                <label
                  className="flex items-center gap-1 text-sm font-medium mb-1"
                  style={{ color: 'var(--theme-card-text)' }}
                >
                  <Icon name="home" className="w-3.5 h-3.5" />
                  Tela inicial
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--theme-muted-text)' }}>
                  Para onde quem tem este nível cai ao abrir o sistema (web e PWA). Só aparecem telas cujo módulo o nível pode ler.
                </p>
                <Select
                  data-testid="level-form-landing-page"
                  value={formData.landingPage ?? ''}
                  onChange={(value) => handleFieldChange('landingPage', value || undefined)}
                  placeholder="Padrão (Dashboard de atendimento)"
                  groups={landingPageGroups}
                />
                <p
                  className="text-xs mt-2"
                  style={{ color: !sellerAllowed ? '#b45309' : 'var(--theme-muted-text)' }}
                  data-testid="level-form-landing-page-hint"
                >
                  {!sellerAllowed && (
                    <>Conceda <b>leitura no CRM</b> abaixo para liberar o CRM e Minhas Vendas como tela inicial.</>
                  )}
                  {sellerAllowed && sellerSelected && (
                    <>Exige permissão de <b>leitura no CRM</b> — já concedida neste nível.</>
                  )}
                  {sellerAllowed && !sellerSelected && (
                    <>Opções de módulo ficam disponíveis apenas com permissão de leitura ativa; "Meus checklists" está sempre disponível, porque a tela não exige permissão.</>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* 2. Permissions Section */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{
              backgroundColor: 'var(--theme-card-background)',
              borderColor: 'var(--theme-card-border)',
            }}
          >
            <div
              className="flex items-start gap-3 px-[18px] py-[15px] border-b"
              style={{ borderColor: 'var(--theme-card-border)' }}
            >
              <div
                className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: 'var(--theme-primary-background)',
                  color: 'var(--theme-primary)',
                }}
              >
                <Icon name="lock" className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold" style={{ color: 'var(--theme-card-text)' }}>
                  Permissões de Acesso
                </h2>
                <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                  Selecione os módulos e configure as ações e o escopo de cada um
                </p>
              </div>
            </div>

            <div className="p-[18px]">
              <PermissionSelector
                permissions={formData.permissions as Record<string, ModulePermission>}
                onChange={(permissions) => handleFieldChange('permissions', permissions)}
                disabled={createMutation.isPending || updateMutation.isPending}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

LevelFormPage.displayName = 'LevelFormPage';
